import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { layoutLocators } from '@locators/layout';
import classNames from 'classnames';
import { Icon } from '@servicetitan/design-system';
import styles from './Draggable.module.css';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import { inboxLocators } from '@locators/inbox';

interface DraggableProps {
  children: React.ReactNode;
  id: string;
  calculateAbsolutePosition: (
    x: number,
    y: number,
  ) => Pick<React.CSSProperties, 'bottom' | 'left' | 'top' | 'right'>;
}

/**
 * Draggable component that allows its children to be dragged within the bounds of the parent <Droppable /> component.
 * This draggable component uses absolute positioning and should be have no parents with relative positioning aside from the <Droppable /> component.
 *
 * @param {string} id - The unique identifier for the Draggable component.
 * @param {Function} calculateAbsolutePosition - The function to calculate the absolute position of the Draggable component.
 */
const Draggable = ({
  children,
  id,
  calculateAbsolutePosition,
}: DraggableProps) => {
  const { callBarPositionDeltas } = useInboxState();

  let x = 0,
    y = 0;

  if (callBarPositionDeltas[id]) {
    const { x: deltaX, y: deltaY } = callBarPositionDeltas[id];
    x = deltaX;
    y = deltaY;
  }

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id,
    });
  const transformStyle = transform
    ? {
        transform: CSS.Translate.toString(transform),
      }
    : undefined;

  const positionStyle = calculateAbsolutePosition(x, y);

  return (
    <div
      id={layoutLocators.draggable}
      className={classNames(`position-absolute`, styles.draggable, {
        'cursor-grabbing': isDragging,
      })}
      // we have to use inline styles here because the position and the transform styles is calculated dynamically
      style={{
        // Max i32/z-index value to prevent anything from cover it
        zIndex: 2147483647,
        ...positionStyle,
        ...transformStyle,
      }}
      ref={setNodeRef}
    >
      <div className='position-relative'>
        <Icon
          className={classNames(
            'c-neutral-70',
            'position-absolute',
            'top-50',
            'left-0',
            'translate-y--50',
            'z-notification',
            'm-l-1',
            { 'cursor-grab': !isDragging },
            styles.draggableIcon,
          )}
          name='drag_indicator'
          id={inboxLocators.callBar.dragHandle}
          {...listeners}
          {...attributes}
        />
        {children}
      </div>
    </div>
  );
};

export default Draggable;
